<template>
  <article :class="[bgColor, textSize]" class="overflow-x-hidden">
    <div class="flex justify-between items-start py-4 px-3 md:px-6">
      <div class="flex-auto relative pr-4">
        <h1 class="font-bold text-xl lg:text-3xl leading-tight max-w-3xl" v-html="title"></h1>
        <p class="mt-2">{{ author }}</p>
      </div>
      <div class="md:pt-2">
        <a @click.prevent="navBack" :href="back" class="print:hidden">
          <close/>
        </a>
      </div>
    </div>
    <div class="border-t border-gray-400 py-4 px-3 md:px-6 p-max-w-xl">
      <div class="md:flex items-start mt-3">
        <ul v-if="!sharing" class="flex-none">
          <li v-for="tax in sammlung" :key="tax.id" :class="`text-${tax.slug}`" class="lowercase inline commas">
            <anchor-wrap :url="tax.link">{{ tax.name }}</anchor-wrap>
          </li>
        </ul>
        <div class="print:hidden relative flex-auto hidden md:block">
          <div :class="{ 'opacity-0': sharing || texting }" class="flex justify-end transition-opacity duration-150">
            <button @click="resize" class="btn"><span class="text-small">A </span> A</button>
            <button @click="share" class="btn">Teilen</button>
            <button @click="print" class="btn hidden md:block">PDF</button>
          </div>
          <div :class="{ 'opacity-0 pointer-events-none': !sharing }" class="absolute right-0 abs-top transition-opacity duration-150">
            <sharing :title="post.title.rendered" @cancel="sharing = false"/>
          </div>
          <div :class="{ 'opacity-0 pointer-events-none': !texting }" class="absolute right-0 abs-top transition-opacity duration-150 flex items-center">
            <button @click="sizing = 0" :class="{ current: sizing === 0 }" class="btn text-xs"><span class="text-shrink">A </span> <span class="text-grow">A</span></button>
            <button @click="sizing = 1" :class="{ current: sizing === 1 }" class="btn text-base"><span class="text-shrink">A </span> <span class="text-grow">A</span></button>
            <button @click="sizing = 2" :class="{ current: sizing === 2 }" class="btn text-xl"><span class="text-shrink">A </span> <span class="text-grow">A</span></button>
            <button @click="texting = false" class="btn h-11"><close :stroke="true" width="18"/></button>
          </div>
        </div>
      </div>
      <div class="post-content mt-6">
        <div v-if="content.length">
          <div v-for="(row, i) in content" :key="i">
            <content-row :row="row"/>
          </div>
        </div>
        <div v-else v-html="post.content.rendered" class="font-thin mb-4"></div>
      </div>

      <ul v-if="tags.length" class="">
        <li class="inline">
          Tags:
        </li>
        <li v-for="tax in tags" :key="tax.id" :class="`text-${tax.slug}`" class="inline commas text-gray-600">
          <anchor-wrap :url="tax.link">{{ tax.name }}</anchor-wrap>
        </li>
      </ul>
    </div>
    <div v-show="hasFootnotes" class="border-t border-gray-400 py-4 px-3 md:px-6 p-max-w-xl">
      <h3 class="text-2xl font-thin mb-4">Fußnoten</h3>
      <div id="footnotes" class="text-sm font-thin -mx-2"></div>
    </div>
    <div v-if="showCitation" class="border-t border-gray-400 py-4 px-3 md:px-6">
      <h3 class="text-2xl font-thin mb-2">Zitation</h3>
      <p class="font-thin">{{ citeAuthor }} ({{ year }}). <em>{{ title }}</em>. Journal der grund_schule der künste, herausgegeben von Kirsten Winderlich. [letzter Zugriff: {{ lastUpdate }}]; {{ url }}</p>
      <!-- Autor*in (Jahr). Artikel-Titel. Journal der grund_schule der künste, herausgegeben von Kirsten Winderlich. [letzter Zugriff: XX.XX.XX]; URL -->
      <span :title="cite" class="Z3988"/>
    </div>
    <div class="border-t border-gray-400 py-4 px-3 md:px-6 p-max-w-xl hidden md:block print:hidden">
      <div class="flex items-center">
        <div v-if="!sharing2" class="text-2xl flex-auto font-bold">Aktionen</div>
        <div class="relative flex-auto">
          <div :class="{ 'opacity-0': sharing2 }" class="flex justify-end transition-opacity duration-150">
            <button @click="share2" class="btn">Teilen</button>
            <button @click="print" class="btn">PDF</button>
          </div>
          <div :class="{ 'opacity-0 pointer-events-none': !sharing2 }" class="absolute right-0 abs-top transition-opacity duration-150">
            <sharing :title="post.title.rendered" @cancel="sharing2 = false"/>
          </div>
        </div>
      </div>
    </div>
    <div class="border-t border-gray-400 py-4 px-3 md:px-6 p-max-w-xl print:hidden">
      <div class="flex items-center">
        <div class="my-1 text-2xl flex-auto font-bold">Verwandte Artikel</div>
      </div>
    </div>
    <div class="bg-white print:hidden border-t border-gray-400">
      <div class="flex flex-wrap">
        <post-card v-for="article in related" :key="article.id" :post="article"/>
      </div>
    </div>
  </article>
</template>

<script>
  import store from '@/store'
  import { format } from 'date-fns/esm'
  import { de } from 'date-fns/esm/locale'
  import AnchorWrap from '@/components/AnchorWrap'
  import ContentRow from '@/components/ContentRow'
  import PostCard from '@/components/PostCard'
  import Sharing from '@/components/Sharing'
  import Close from '@/components/icons/Close'
  import { setupAnnotations } from '@/utils'

  const textSizes = {
    0: 'text-shrink',
    1: '',
    2: 'text-grow'
  }

  export default {
    name: 'Post',
    metaInfo () {
      return {
        title: this.title
      }
    },
    data () {
      return {
        hasFootnotes: false,
        related: [],
        sizing: 1,
        texting: false,
        sharing: false,
        sharing2: false
      }
    },
    computed: {
      post () {
        return store.state.post
      },
      title () {
        if (!this.post) return ''
        return this.post.title.rendered
      },
      lastUpdate () {
        const modified = new Date()
        return format(modified, 'dd.MM.yyyy')
      },
      sammlung () {
        if (!this.post) return []
        return this.post._embedded['wp:term'].reduce((a, c) => {
          return a.concat(...c.filter(t => t.taxonomy === 'category'))
        }, [])
      },
      tags () {
        if (!this.post) return []
        return this.post._embedded['wp:term'].reduce((a, c) => {
          return a.concat(...c.filter(t => t.taxonomy === 'post_tag'))
        }, [])
      },

      bgColor () {
        const { category } = this.$route.params
        if (!category) return
        return `bg-${category}`
      },
      textSize () {
        return textSizes[this.sizing]
      },

      author () {
        if (!this.post) return
        return this.post.acf.autor || this.post._embedded.author[0]?.name
      },
      citeAuthor () {
        if (!this.author) return
        const seperator = this.author.includes(';') ? ';' : ','
        const list = this.author.split(seperator).map(s => s.trim())
        const formatted = []

        for (const auth of list) {
          const divider = auth.includes(',') ? ',' : ' '
          const pts = auth.split(divider).map(s => s?.replace(/,/g, '').trim())
          if (divider !== ',') {
            pts.reverse()
          }
          formatted.push(pts.join(', '))
        }

        return formatted.length && formatted.join('; ')
      },
      date () {
        if (!this.post) return
        const d = new Date(this.post.date)
        return format(d, 'MMM yyyy', { locale: de })
      },
      year () {
        if (!this.post) return
        const published = new Date(this.post.date)
        return format(published, 'yyyy')
      },
      content () {
        if (!this.post) return []
        return this.post.acf?.content || []
      },
      back () {
        return store.state.from ? store.state.from.fullPath : '/'
      },
      url () {
        return window.location.href
      },
      fbShare () {
        return `https://www.facebook.com/sharer/sharer.php?u=${this.url}`
      },
      cite () {
        const params = new URLSearchParams({
          ctx_ver: 'Z39.88-2004',
          rft_val_fmt: 'info:ofi/fmt:kev:mtx:journal',
          'rft.genre': 'article',
          'rft.atitle': this.title,
          'rft.jtitle': 'Journal der grund_schule der künste',
          'rft.date': this.year,
          rft_id: this.url
        })

        if (this.author) {
          const list = this.author.split(', ')
          for (const auth of list) {
            params.append('rft.au', auth)
          }
        }

        return params.toString()
      },
      showCitation () {
        return this.post.acf?.citation
      }
    },
    methods: {
      setupAnnotations,

      navBack () {
        if (!store.state.from.name) {
          this.$router.push('/')
        } else {
          this.$router.go(-1)
        }
      },
      getRelated () {
        this.$store.dispatch('getRelated', this.post).then(({ data }) => {
          this.related = data
        })
      },
      print () {
        'print' in window && window.print()
      },

      resize () {
        this.texting = true
      },
      share () {
        if (!this.navShare()) {
          this.sharing = true
        }
      },
      share2 () {
        if (!this.navShare()) {
          this.sharing2 = true
        }
      },
      navShare () {
        if (typeof window.navigator.share === 'function') {
          window.navigator.share({ url: this.url }).catch(() => {})
          return true
        } else {
          return false
        }
      }
    },
    mounted () {
      this.setupAnnotations()
      setTimeout(() => {
        this.getRelated()
      }, 850)
    },
    beforeRouteEnter (to, from, next) {
      const { slug } = to.params
      store.dispatch('getPostBySlug', slug).then(next)
    },
    beforeRouteUpdate (to, from, next) {
      const { slug } = to.params
      store.dispatch('getPostBySlug', slug).then(() => {
        setTimeout(() => {
          this.getRelated()
          this.setupAnnotations()
        }, 850)
        next()
      })
    },
    components: { AnchorWrap, ContentRow, PostCard, Sharing, Close }
  }
</script>
